import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notificationsApi, type Notification } from '@/lib/api';
import { Badge, severityColor } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { CheckCheck, ChevronRight } from 'lucide-react';

function timeAgo(dt: string) {
  const diff = Date.now() - new Date(dt).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const ENTITY_ROUTES: Record<string, string> = {
  work_order: '/work-orders',
  part:       '/parts',
  equipment:  '/equipment',
};

const ENTITY_LABELS: Record<string, string> = {
  work_order: 'View work order',
  part:       'View part',
  equipment:  'View equipment',
};

function entityUrl(n: Notification): string | null {
  if (!n.entity_type || !n.entity_id) return null;
  const base = ENTITY_ROUTES[n.entity_type];
  return base ? `${base}/${n.entity_id}` : null;
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'page', page],
    queryFn: () => notificationsApi.list(page),
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.read(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAll = useMutation({
    mutationFn: () => notificationsApi.readAll(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  function handleClick(n: Notification, url: string | null) {
    if (!n.is_read) markRead.mutate(n.id);
    if (url) navigate(url);
  }

  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      <Card>
        <CardHeader
          title={`Notifications (${data?.pagination.total ?? 0})`}
          action={
            <Button size="sm" variant="outline" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
              <CheckCheck size={14} /> Mark all read
            </Button>
          }
        />
        <CardBody className="p-0">
          {isLoading ? (
            <p className="px-5 py-8 text-center text-slate-400">Loading…</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data?.data.map((n: Notification) => {
                const url = entityUrl(n);
                return (
                  <li
                    key={n.id}
                    onClick={() => handleClick(n, url)}
                    className={cn(
                      'flex items-start gap-3 px-5 py-3.5 transition-colors',
                      !n.is_read && 'bg-brand-50/50',
                      url ? 'cursor-pointer hover:bg-slate-50' : (!n.is_read && 'cursor-pointer hover:bg-brand-50'),
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Badge color={severityColor(n.severity)}>{n.severity}</Badge>
                        <span className="text-xs text-slate-400">{timeAgo(n.created_at)}</span>
                        {!n.is_read && (
                          <span className="ml-auto w-2 h-2 rounded-full bg-brand-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className={`text-sm font-medium ${url ? 'text-brand-700' : 'text-slate-900'}`}>
                        {n.title}
                      </p>
                      {n.body && <p className="text-xs text-slate-500 mt-0.5">{n.body}</p>}
                      {url && (
                        <p className="text-xs text-brand-500 mt-0.5">
                          {ENTITY_LABELS[n.entity_type ?? ''] ?? 'View'} →
                        </p>
                      )}
                    </div>
                    {url && <ChevronRight size={16} className="text-slate-300 mt-1 flex-shrink-0" />}
                    {!url && !n.is_read && (
                      <button
                        onClick={e => { e.stopPropagation(); markRead.mutate(n.id); }}
                        className="text-xs text-brand-600 hover:underline flex-shrink-0 mt-0.5"
                      >
                        Mark read
                      </button>
                    )}
                  </li>
                );
              })}
              {data?.data.length === 0 && (
                <li className="px-5 py-8 text-center text-slate-400">No notifications.</li>
              )}
            </ul>
          )}
        </CardBody>
      </Card>

      {(data?.pagination.total ?? 0) > 20 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>Prev</Button>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page * 20 >= (data?.pagination.total ?? 0)}>Next</Button>
        </div>
      )}
    </div>
  );
}
